import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Plus, Search, X, Phone, Mail, Building2, DollarSign, Calendar, Clock, Zap, ChevronRight } from "lucide-react";
import { LEADS, PIPELINE_STAGES } from "@/data/dummyData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sourceColors = { meta: "bg-blue-100 text-blue-700", google: "bg-red-100 text-red-700", whatsapp: "bg-green-100 text-green-700" };

function LeadDetailSheet({ lead, onClose }) {
  const [aiInsight, setAiInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const getInsight = async () => {
    setAiLoading(true);
    setAiInsight("");
    try {
      const res = await fetch(`${API}/ai/lead-insight`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lead_name: lead.name, stage: lead.stage, property_interest: lead.propertyInterest,
          budget: lead.budget, source: lead.source, score: lead.score,
          notes: lead.notes, days_in_pipeline: lead.daysInPipeline,
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            const text = line.slice(6);
            if (text === "[DONE]") { setAiLoading(false); return; }
            setAiInsight(p => p + text);
          }
        }
      }
    } catch {
      setAiInsight("Error fetching insight.");
    } finally {
      setAiLoading(false);
    }
  };

  const stage = PIPELINE_STAGES.find(s => s.id === lead.stage);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <motion.div
        className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl"
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        onClick={e => e.stopPropagation()}
        data-testid="lead-detail-sheet"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white font-bold">
              {lead.avatar}
            </div>
            <div>
              <p className="font-heading font-bold text-slate-900">{lead.name}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${stage?.color || "bg-slate-100 text-slate-700"}`}>
                {stage?.label || lead.stage}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid="close-lead-sheet">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl p-3">
              <Phone className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-400">Phone</p>
                <p className="text-xs font-semibold text-slate-800">{lead.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl p-3">
              <Mail className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-400">Email</p>
                <p className="text-xs font-semibold text-slate-800 truncate">{lead.email}</p>
              </div>
            </div>
          </div>

          {/* Key Details */}
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Lead Details</p>
            <div className="space-y-2.5">
              {[
                { icon: Building2, label: "Property", value: lead.propertyInterest },
                { icon: DollarSign, label: "Budget", value: lead.budget },
                { icon: Users, label: "Config", value: lead.config },
                { icon: Calendar, label: "Source", value: lead.source.toUpperCase() },
                { icon: Clock, label: "Created", value: lead.createdAt },
                { icon: Users, label: "Assigned To", value: lead.assignedTo || "Unassigned" },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <item.icon className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs text-slate-500">{item.label}</span>
                  </div>
                  <span className="text-xs font-semibold text-slate-800">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* AI Score */}
          <div className="bg-blue-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-700">AI Lead Score</p>
              <span className="font-heading text-2xl font-bold text-blue-600">{lead.score}/100</span>
            </div>
            <div className="w-full bg-blue-100 rounded-full h-2 mb-2">
              <div className={`h-2 rounded-full ${lead.score >= 80 ? "bg-emerald-500" : lead.score >= 60 ? "bg-blue-600" : "bg-yellow-500"}`} style={{ width: `${lead.score}%` }} />
            </div>
            <p className="text-xs text-blue-600 font-medium">{lead.score >= 80 ? "High Intent — Ready to Book" : lead.score >= 60 ? "Medium Intent — Needs Nurturing" : "Low Intent — Early Stage"}</p>
          </div>

          {/* Notes */}
          {lead.notes && (
            <div className="bg-amber-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-800 mb-1">Notes</p>
              <p className="text-xs text-amber-900 leading-relaxed">{lead.notes}</p>
            </div>
          )}

          {/* AI Insight */}
          <div>
            <button
              onClick={getInsight}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
              data-testid="lead-ai-insight-btn"
            >
              <Zap className="w-4 h-4" />
              {aiLoading ? "Generating..." : "Get AI Sales Insight"}
            </button>
            {(aiInsight || aiLoading) && (
              <div className="mt-3 bg-slate-50 rounded-xl p-4 min-h-16">
                {aiLoading && !aiInsight ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    Analyzing lead...
                  </div>
                ) : (
                  <p className="text-sm text-slate-700 ai-stream leading-relaxed">{aiInsight}</p>
                )}
              </div>
            )}
          </div>

          {/* Timeline */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Activity</p>
            <div className="space-y-2 relative">
              <div className="absolute left-3 top-3 bottom-3 w-px bg-slate-200" />
              {[
                { label: `${lead.source.toUpperCase()} Ad Clicked`, time: lead.createdAt, color: "bg-blue-400" },
                { label: "WhatsApp Connected", time: lead.createdAt, color: "bg-green-400" },
                { label: `AI Lead Score: ${lead.score}`, time: "Auto-calculated", color: "bg-purple-400" },
                { label: `Stage: ${PIPELINE_STAGES.find(s => s.id === lead.stage)?.label}`, time: lead.lastActivity, color: "bg-orange-400" },
              ].map((act, i) => (
                <div key={i} className="flex items-center gap-3 pl-7">
                  <div className={`absolute left-1.5 w-3 h-3 rounded-full ${act.color} border-2 border-white`} />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-800">{act.label}</p>
                    <p className="text-xs text-slate-400">{act.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function Leads() {
  const [search, setSearch] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [showStageFilter, setShowStageFilter] = useState("all");

  const filteredLeads = LEADS.filter(l => {
    const matchSearch = l.name.toLowerCase().includes(search.toLowerCase()) ||
      l.propertyInterest.toLowerCase().includes(search.toLowerCase());
    const matchStage = showStageFilter === "all" || l.stage === showStageFilter;
    return matchSearch && matchStage;
  });

  const stageLeads = (stageId) => filteredLeads.filter(l => l.stage === stageId);

  const totalByStage = PIPELINE_STAGES.map(s => ({ ...s, count: LEADS.filter(l => l.stage === s.id).length }));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Leads Pipeline</h2>
          <p className="text-slate-500 text-sm mt-0.5">{LEADS.length} total leads · {LEADS.filter(l => l.stage === "booked").length} booked this month</p>
        </div>
        <div className="sm:ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search leads..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 w-48"
              data-testid="leads-search"
            />
          </div>
          <button className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-3.5 py-2 rounded-lg transition-colors" data-testid="add-lead-btn">
            <Plus className="w-4 h-4" /> Add Lead
          </button>
        </div>
      </div>

      {/* Pipeline Summary */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {totalByStage.map(stage => (
          <button
            key={stage.id}
            onClick={() => setShowStageFilter(showStageFilter === stage.id ? "all" : stage.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${showStageFilter === stage.id ? stage.color + " ring-2 ring-blue-500" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          >
            <div className={`w-2 h-2 rounded-full ${stage.dot}`} />
            {stage.label} ({stage.count})
          </button>
        ))}
        {showStageFilter !== "all" && (
          <button onClick={() => setShowStageFilter("all")} className="px-2 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-100">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4 min-h-96">
        {PIPELINE_STAGES.map(stage => {
          const leads = stageLeads(stage.id);
          return (
            <div key={stage.id} className="shrink-0 w-52" data-testid={`kanban-column-${stage.id}`}>
              {/* Column Header */}
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${stage.dot}`} />
                  <span className="text-xs font-semibold text-slate-700">{stage.label}</span>
                </div>
                <span className="text-xs bg-slate-100 text-slate-500 font-semibold px-1.5 py-0.5 rounded-full">{leads.length}</span>
              </div>

              {/* Lead Cards */}
              <div className="space-y-2.5">
                {leads.map((lead, i) => (
                  <motion.div
                    key={lead.id}
                    className="bg-white rounded-xl border border-slate-200 p-3 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all group"
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    onClick={() => setSelectedLead(lead)}
                    data-testid={`lead-card-${lead.id}`}
                    whileHover={{ scale: 1.01 }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                        {lead.avatar}
                      </div>
                      <p className="text-xs font-semibold text-slate-800 truncate">{lead.name}</p>
                    </div>
                    <p className="text-xs text-slate-500 truncate mb-2">{lead.propertyInterest}</p>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sourceColors[lead.source] || "bg-slate-100 text-slate-600"}`}>
                        {lead.source}
                      </span>
                      <span className="text-xs font-bold text-blue-600">{lead.score}</span>
                    </div>
                    {lead.budget && (
                      <p className="text-xs text-slate-400 mt-1.5">{lead.budget}</p>
                    )}
                    {lead.daysInPipeline > 0 && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <Clock className="w-3 h-3 text-slate-300" />
                        <span className="text-xs text-slate-400">{lead.daysInPipeline}d</span>
                      </div>
                    )}
                  </motion.div>
                ))}

                {/* Empty State */}
                {leads.length === 0 && (
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-400">No leads</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lead Detail Sheet */}
      <AnimatePresence>
        {selectedLead && <LeadDetailSheet lead={selectedLead} onClose={() => setSelectedLead(null)} />}
      </AnimatePresence>
    </div>
  );
}
